import { EnemyManager } from '../enemies/EnemyManager';
import { EnemyType } from '../enemies/EnemyType';
import { Enemy } from '../enemies/Enemy';
import { WaveConfig } from './WaveConfig';
import { WaveType } from './WaveManager';

/**
 * Represents a single wave of enemies
 */
export class Wave {
  private config: WaveConfig;
  private enemyManager: EnemyManager;
  private enemiesSpawned: Record<string, number> = {};
  private enemiesRemaining: Record<string, number> = {};
  private totalEnemiesSpawned: number = 0;
  private totalEnemiesRemaining: number = 0;
  private spawnTimer: number = 0;
  private active: boolean = false;
  private complete: boolean = false;
  private waveTime: number = 0; // Time elapsed since wave started
  private bossSpawned: boolean = false; // Flag to track if boss has been spawned
  
  /**
   * Creates a new wave
   * @param config The wave configuration
   * @param enemyManager The enemy manager to use
   */
  constructor(config: WaveConfig, enemyManager: EnemyManager) {
    this.config = config;
    this.enemyManager = enemyManager;
    
    // Initialize counters
    this.resetCounters();
  }
  
  /**
   * Resets the enemy counters
   */
  private resetCounters(): void {
    // Reset spawned counters
    this.enemiesSpawned = {};
    this.totalEnemiesSpawned = 0;
    
    // Reset remaining counters
    this.enemiesRemaining = {};
    for (const type in this.config.enemyCounts) {
      this.enemiesRemaining[type] = this.config.enemyCounts[type as EnemyType] || 0;
    }
    this.totalEnemiesRemaining = this.config.totalEnemies;
  }
  
  /**
   * Starts the wave
   */
  public start(): void {
    // Reset counters
    this.resetCounters();
    
    // Reset timer
    this.spawnTimer = 0;
    this.waveTime = 0;
    this.bossSpawned = false;
    
    // Set active
    this.active = true;
    this.complete = false;
    
    console.log(`Wave ${this.config.waveNumber} (${this.config.waveType}) started with ${this.config.totalEnemies} enemies`);
  }
  
  /**
   * Updates the wave
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    if (!this.active || this.complete) return;
    
    // Update wave time
    this.waveTime += deltaTime;
    
    // Update spawn timer
    this.spawnTimer += deltaTime;
    
    // Handle wave type-specific behavior
    this.handleWaveTypeSpecificBehavior(deltaTime);
    
    // Check if we should spawn an enemy
    const spawnInterval = 1 / this.config.spawnRate;
    if (this.spawnTimer >= spawnInterval) {
      this.spawnTimer -= spawnInterval;
      this.spawnNextEnemy();
    }
    
    // Periodically update the actual count of remaining enemies
    // This ensures the count stays accurate even if enemies die from other causes
    this.updateRemainingEnemiesCount();
    
    // Check if wave is complete
    this.checkCompletion();
  }
  
  /**
   * Updates the count of remaining enemies based on the actual number of active enemies
   */
  public updateRemainingEnemiesCount(): void {
    // The totalEnemiesRemaining should reflect the actual state:
    // Active enemies + enemies yet to spawn
    const activeEnemies = this.enemyManager.getEnemyCount();
    const yetToSpawn = Math.max(0, this.config.totalEnemies - this.totalEnemiesSpawned);
    
    // Update the internal counter to match reality
    this.totalEnemiesRemaining = activeEnemies + yetToSpawn;
  }
  
  /**
   * Handles wave type-specific behavior
   * @param deltaTime Time since last update in seconds
   */
  private handleWaveTypeSpecificBehavior(deltaTime: number): void {
    switch (this.config.waveType) {
      case WaveType.BOSS:
        // For boss waves, spawn the boss after a delay
        if (!this.bossSpawned && this.waveTime >= 5.0 && this.totalEnemiesSpawned < this.config.totalEnemies) {
          // Spawn the boss
          this.spawnBoss();
          this.bossSpawned = true;
        }
        break;
        
      case WaveType.HORDE:
        // For horde waves, occasionally spawn enemies in bursts
        if (Math.random() < 0.01 * deltaTime * 60) { // ~1% chance per second
          this.spawnEnemyBurst();
        }
        break;
        
      case WaveType.ELITE:
        // For elite waves, enemies are stronger but no special behavior needed
        break;
        
      case WaveType.SPECIAL:
        // For special waves, could implement unique behaviors
        break;
    }
  }
  
  /**
   * Spawns a burst of enemies at once
   */
  private spawnEnemyBurst(): void {
    // Only spawn bursts if we have enemies left to spawn
    if (this.totalEnemiesSpawned >= this.config.totalEnemies) return;
    
    // Determine how many enemies to spawn in the burst
    const maxBurstSize = 3;
    const remainingToSpawn = this.config.totalEnemies - this.totalEnemiesSpawned;
    const burstSize = Math.min(maxBurstSize, remainingToSpawn);
    
    console.log(`Spawning burst of ${burstSize} enemies`);
    
    // Spawn the burst of enemies
    for (let i = 0; i < burstSize; i++) {
      this.spawnNextEnemy();
    }
  }
  
  /**
   * Spawns the boss enemy
   */
  private spawnBoss(): void {
    // Check if we have a boss to spawn
    if (this.enemiesRemaining[EnemyType.BOSS] <= 0) return;
    
    // Spawn the boss
    const boss = this.enemyManager.spawnBoss();
    
    if (boss) {
      // Update counters
      this.enemiesSpawned[EnemyType.BOSS] = (this.enemiesSpawned[EnemyType.BOSS] || 0) + 1;
      this.enemiesRemaining[EnemyType.BOSS]--;
      this.totalEnemiesSpawned++;
      
      console.log(`Spawned BOSS enemy (${this.totalEnemiesSpawned}/${this.config.totalEnemies})`);
    }
  }
  
  /**
   * Spawns the next enemy in the wave
   */
  private spawnNextEnemy(): void {
    // Check if we've spawned all enemies
    if (this.totalEnemiesSpawned >= this.config.totalEnemies) {
      return;
    }
    
    // Determine which type of enemy to spawn
    const enemyType = this.selectNextEnemyType();
    if (!enemyType) return;
    
    // Spawn the enemy
    const enemy = this.enemyManager.spawnEnemy(enemyType);
    
    if (enemy) {
      // Update counters
      this.enemiesSpawned[enemyType] = (this.enemiesSpawned[enemyType] || 0) + 1;
      this.enemiesRemaining[enemyType]--;
      this.totalEnemiesSpawned++;
      
      console.log(`Spawned ${enemyType} enemy (${this.totalEnemiesSpawned}/${this.config.totalEnemies})`);
    }
  }
  
  /**
   * Selects the next enemy type to spawn
   * @returns The enemy type to spawn, or null if no enemies are left
   */
  private selectNextEnemyType(): EnemyType | null {
    // For boss waves, prioritize spawning regular enemies first, then the boss at the end
    if (this.config.waveType === WaveType.BOSS && !this.bossSpawned) {
      // Don't spawn the boss until we've spawned most of the regular enemies
      const regularEnemiesSpawned = this.totalEnemiesSpawned - (this.enemiesSpawned[EnemyType.BOSS] || 0);
      const totalRegularEnemies = this.config.totalEnemies - (this.config.enemyCounts[EnemyType.BOSS] || 0);
      
      if (regularEnemiesSpawned < totalRegularEnemies) {
        // Prioritize spawning regular enemies
        return this.selectRegularEnemyType();
      }
    }
    
    // Get all enemy types with remaining enemies
    const availableTypes: EnemyType[] = [];
    
    for (const type in this.enemiesRemaining) {
      if (this.enemiesRemaining[type] > 0) {
        // For boss waves, only add the boss if we're ready to spawn it
        if (type === EnemyType.BOSS && !this.bossSpawned) {
          continue;
        }
        
        availableTypes.push(type as EnemyType);
      }
    }
    
    if (availableTypes.length === 0) {
      return null;
    }
    
    // Adjust spawn probabilities based on wave type
    let selectedType: EnemyType;
    
    switch (this.config.waveType) {
      case WaveType.HORDE:
        // For horde waves, prioritize grunts
        if (this.enemiesRemaining[EnemyType.GRUNT] > 0 && Math.random() < 0.7) {
          selectedType = EnemyType.GRUNT;
        } else {
          // Randomly select from available types
          const randomIndex = Math.floor(Math.random() * availableTypes.length);
          selectedType = availableTypes[randomIndex];
        }
        break;
        
      case WaveType.ELITE:
        // For elite waves, prioritize brutes
        if (this.enemiesRemaining[EnemyType.BRUTE] > 0 && Math.random() < 0.6) {
          selectedType = EnemyType.BRUTE;
        } else {
          // Randomly select from available types
          const randomIndex = Math.floor(Math.random() * availableTypes.length);
          selectedType = availableTypes[randomIndex];
        }
        break;
        
      default:
        // Randomly select from available types
        const randomIndex = Math.floor(Math.random() * availableTypes.length);
        selectedType = availableTypes[randomIndex];
        break;
    }
    
    return selectedType;
  }
  
  /**
   * Selects a regular (non-boss) enemy type to spawn
   * @returns The enemy type to spawn, or null if no regular enemies are left
   */
  private selectRegularEnemyType(): EnemyType | null {
    // Get all regular enemy types with remaining enemies
    const availableTypes: EnemyType[] = [];
    
    for (const type in this.enemiesRemaining) {
      if (type !== EnemyType.BOSS && this.enemiesRemaining[type] > 0) {
        availableTypes.push(type as EnemyType);
      }
    }
    
    if (availableTypes.length === 0) {
      return null;
    }
    
    // Randomly select from available types
    const randomIndex = Math.floor(Math.random() * availableTypes.length);
    return availableTypes[randomIndex];
  }
  
  /**
   * Handles an enemy being defeated
   * @param enemy The defeated enemy
   */
  public enemyDefeated(enemy: Enemy): void {
    // Update remaining enemies
    this.totalEnemiesRemaining--;
    
    // Update the actual count of remaining enemies
    this.updateRemainingEnemiesCount();

    // Check if wave is complete
    this.checkCompletion();
  }
  
  /**
   * Checks if the wave is complete
   */
  private checkCompletion(): void {
    // Update the remaining enemies count first
    this.updateRemainingEnemiesCount();
    
    // Wave is complete when all enemies have been spawned and all active enemies are defeated
    if (this.totalEnemiesSpawned >= this.config.totalEnemies && this.totalEnemiesRemaining <= 0) {
      this.complete = true;
      this.active = false;
      
      console.log(`Wave ${this.config.waveNumber} completed`);
    }
  }
  
  /**
   * Gets the number of remaining enemies
   * @returns The number of remaining enemies
   */
  public getRemainingEnemies(): number {
    // Calculate remaining enemies as the sum of:
    // 1. Enemies that are currently active in the EnemyManager
    // 2. Enemies that are yet to be spawned
    const activeEnemies = this.enemyManager.getEnemyCount();
    const yetToSpawn = Math.max(0, this.config.totalEnemies - this.totalEnemiesSpawned);
    const total = activeEnemies + yetToSpawn;
    
    return total;
  }
  
  /**
   * Gets the number of enemies spawned
   * @returns The number of enemies spawned
   */
  public getEnemiesSpawned(): number {
    return this.totalEnemiesSpawned;
  }
  
  /**
   * Gets the wave configuration
   * @returns The wave configuration
   */
  public getConfig(): WaveConfig {
    return this.config;
  }
  
  /**
   * Checks if the wave is active
   * @returns True if the wave is active
   */
  public isActive(): boolean {
    return this.active;
  }
  
  /**
   * Checks if the wave is complete
   * @returns True if the wave is complete
   */
  public isComplete(): boolean {
    return this.complete;
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    // Nothing to dispose of currently
  }
} 